import { getChordFlavours } from '../../const/chordFlavours';
import { useAppSelector } from '../../global/hooks';
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalFoot,
	ModalHead,
	SaveButton
} from '../../global/styles';
import useStateWithFocusRef from '../../hooks/useStateWithFocusRef';
import {
	selectActiveChord,
	selectActiveSectionId,
	selectView,
	setActiveChordId,
	setActiveSectionId,
	setView
} from '../../store/appSlice';
import { addChord, updateChord } from '../../store/sectionsSlice';
import DeleteChord from './DeleteChord';
import FlavourSelect from './FlavourSelect';
import KeySelect from './KeySelect';
import LengthInput from './LengthInput';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useClickAway } from 'react-use';

export default function EditChord() {
	// const hasExtraChords = useAppSelector(selectHasFeature(FeatureEnum.extrachords));
	const flavourIds = getChordFlavours(true).map(f => f.id);

	const dispatch = useDispatch();

	const closeModal = () => {
		dispatch(setView('main'));
		dispatch(setActiveSectionId(null));
		dispatch(setActiveChordId(null));
	};
	const sectionId = useAppSelector(selectActiveSectionId);
	const isVisible = useAppSelector(selectView) === 'editChord' && sectionId !== null;
	const activeChord = useAppSelector(selectActiveChord);

	const saveRef = useRef<HTMLButtonElement>(null);

	// state
	const [key, setKey] = useStateWithFocusRef<number>(activeChord?.key ?? 0, saveRef);
	const [flavourId, setFlavourId] = useStateWithFocusRef<string>(
		activeChord?.flavourId ?? flavourIds[0],
		saveRef
	);
	const [length, setLength] = useStateWithFocusRef(activeChord?.length ?? 1, saveRef);

	const isDirty =
		!activeChord ||
		(activeChord &&
			(key !== activeChord.key ||
				flavourId !== activeChord.flavourId ||
				length !== activeChord.length));

	const handleSaveChord = isVisible
		? () => {
				// changes detected
				if (isDirty && activeChord !== null) {
					dispatch(
						updateChord({
							sectionId,
							chord: {
								id: activeChord.id,
								key,
								flavourId,
								length
							}
						})
					);
					// changes detected but no active chord, so new chord
				} else if (isDirty) {
					dispatch(
						addChord({
							sectionId,
							chord: {
								id: Date.now(),
								key,
								flavourId,
								length
							}
						})
					);
				}
				dispatch(setView('main'));
				dispatch(setActiveSectionId(null));
				dispatch(setActiveChordId(null));

				// reset key, flavour and length for next time
				setKey(0);
				setFlavourId(flavourIds[0]);
				setLength(1);
		  }
		: undefined;

	// when first loading, if editing existing chord, load chord data
	useEffect(() => {
		if (isVisible && activeChord !== null) {
			setKey(activeChord.key);
			setFlavourId(activeChord.flavourId);
			setLength(activeChord.length);
		}
	}, [isVisible, activeChord, setKey, setFlavourId, setLength]);

	const ref = useRef(null);
	useClickAway(ref, () => closeModal());
	return isVisible ? (
		<Modal ref={ref}>
			<ModalCloseButton
				onClick={closeModal}
				title="close this modal popup"
				aria-label="close this modal poup"
			/>
			<div>
				<ModalHead>{activeChord !== null ? 'Edit' : 'Add'} Chord</ModalHead>

				<ModalBody>
					<KeySelect value={String(key)} onChange={v => setKey(Number(v))} />
					<FlavourSelect value={flavourId} onChange={setFlavourId} />
					<LengthInput value={length} onChange={setLength} />
					<DeleteChord />
				</ModalBody>
			</div>

			<ModalFoot>
				<SaveButton
					ref={saveRef}
					disabled={!isDirty}
					onClick={handleSaveChord}
					title={isDirty ? undefined : 'No changes detected'}
				>
					{activeChord !== null ? 'Save' : 'Add'} Chord
				</SaveButton>
			</ModalFoot>
		</Modal>
	) : null;
}
